import { useMutation } from '@apollo/client';
import {
  COMPANY_CLAIM_REQUEST,
  CompanyClaim,
  ErrorResponse,
  GET_COMPANY_CLAIM_REQUESTS,
} from '@frontend/data-access';
import { App, Form, Input, Modal } from 'antd';
import useTranslation from 'next-translate/useTranslation';

type Props = {
  isOpenClaim: boolean;
  setIsOpenClaim: (value: boolean) => void;
  companyId: number;
};

const ClaimCompanyProfile = ({
  isOpenClaim,
  setIsOpenClaim,
  companyId,
}: Props) => {
  const { notification } = App.useApp();
  const [form] = Form.useForm();
  const { t } = useTranslation('common');

  const formFieldRules = {
    name: [
      {
        required: true,
        validator: (_: unknown, value: string) =>
          value && value.trim().length > 2
            ? Promise.resolve()
            : Promise.reject(t('institution_name_error')),
      },
    ],
    email: [
      {
        required: true,
        validator: (_: unknown, value: string) =>
          value && value.trim().length > 2
            ? Promise.resolve()
            : Promise.reject(t('work_email_error')),
      },
    ],
    reason: [
      {
        required: true,
        validator: (_: unknown, value: string) =>
          value && value.trim().length > 2
            ? Promise.resolve()
            : Promise.reject(t('reason_error')),
      },
    ],
  };

  const handleOnCompleted = () => {
    form.resetFields();
    setIsOpenClaim(false);
    notification.success({
      message: t('claim_request_sent'),
    });
  };

  const handleError = (error: ErrorResponse) => {
    form.resetFields();
    setIsOpenClaim(false);
    notification.error({
      message: error.message,
    });
  };

  const [claimRequest, { loading }] = useMutation(COMPANY_CLAIM_REQUEST, {
    onCompleted: handleOnCompleted,
    onError: handleError,
    refetchQueries: [
      { query: GET_COMPANY_CLAIM_REQUESTS, variables: { companyId } },
    ],
  });

  const handleFinish = (values: CompanyClaim) => {
    claimRequest({ variables: { input: { ...values, companyId: companyId } } });
  };

  const handleSubmit = () => {
    form.submit();
  };

  return (
    <div>
      <Modal
        title={t('claim_profile')}
        open={isOpenClaim}
        onOk={handleSubmit}
        okButtonProps={{ shape: 'round', loading: loading }}
        cancelButtonProps={{
          shape: 'round',
        }}
        onCancel={() => setIsOpenClaim(false)}
      >
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Form.Item
            name="name"
            label={t('institution_name')}
            rules={formFieldRules.name}
            required
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label={t('work_email')}
            rules={formFieldRules.email}
            required
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="phone"
            label={
              <div>
                {t('phone_number')}{' '}
                <span className="text-gray-700">{t('optional')}</span>
              </div>
            }
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="reason"
            label={t('clarify_reason')}
            rules={formFieldRules.reason}
            required
          >
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ClaimCompanyProfile;
